import type { Components } from 'react-markdown';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { FileText } from '../../../../common/icons/lucide-adapter';
import { ReportWrapper } from '../components/ReportWrapper';

type ReferencesSectionProps = {
  references?: string | null;
};

export const ReferencesSection: React.FC<ReferencesSectionProps> = ({ references }) => {
  if (!references || references.trim() === '') {
    return null;
  }

  return (
    <ReportWrapper title="References" Icon={FileText} showTooltip={false}>
      <div className="prose prose-sm max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {references}
        </ReactMarkdown>
      </div>
    </ReportWrapper>
  );
};

const markdownComponents: Components = {
  p: ({ node, ...props }) => (
    <p className="mb-2 leading-relaxed" style={{ fontSize: '14px' }} {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-inside list-decimal space-y-1" style={{ fontSize: '14px' }} {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-inside list-disc space-y-1" style={{ fontSize: '14px' }} {...props} />
  ),
  li: ({ node, ...props }) => <li style={{ fontSize: '14px' }} {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="text-[0.7rem] font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  table: ({ node, ...props }) => (
    <table className="my-4 w-full border-collapse text-sm" {...props} />
  ),
  thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
  tbody: ({ node, ...props }) => <tbody {...props} />,
  tr: ({ node, ...props }) => <tr className="border-b last:border-b-0" {...props} />,
  th: ({ node, ...props }) => (
    <th className="border border-slate-200 px-3 py-2 text-left font-semibold" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="border border-slate-200 px-3 py-2 align-top" {...props} />
  ),
};
